import { type BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react'
import { toast } from 'sonner'
import { type AxiosError, type AxiosRequestConfig } from 'axios'
import { isEqual } from 'lodash-es'
import { parseISO } from 'date-fns'
import {
  type FlatServiceCursorPaginatedResponseType,
  type ServiceCursorPaginatedResponseType,
  ServiceErrorHandler,
  defaultServicePagination,
} from '@/connect-types/backend/service'
import { customAxios } from '@/utils/axiosHelper'
import config from '@/config'
import { buildUrl } from '@/utils/common'
import { post } from '@/utils/api'
// One-way edge: zion knows nothing about inbox, so there is no cycle here.
import zionApi from '@/state/zion/zion.slice'
import {
  type InboxAssetInitiatePayload,
  type InboxAssetInitiateResponse,
  type InboxAssetType,
  type InboxContextTypes,
  type InboxMessageTemplatePayload,
  type InboxMessageTemplateType,
  type InboxRenderedTemplate,
  type InboxSettingsDataType,
  type InboxTagType,
  type InboxThreadAssigneeType,
  type InboxThreadContactsType,
  type InboxThreadEventType,
  type InboxThreadLastEventV2,
  type InboxThreadsLastEventsV2Response,
  type InboxThreadType,
  type VirtualNumberType,
} from '../entities/inbox/inbox.types'

/*
  php artisan list

  GET|HEAD  / App\Packages\Healthcheck\Controllers\HealthcheckController@index
  POST      _ignition/execute-solution ignition.executeSolution › Spatie\Lara…
  GET|HEAD  _ignition/health-check ignition.healthCheck › Spatie\LaravelIgnit…
  POST      _ignition/update-config ignition.updateConfig › Spatie\LaravelIgn…
  GET|HEAD  health-check App\Packages\Healthcheck\Controllers\HealthcheckCont…
  GET|HEAD  health-check/cache App\Packages\Healthcheck\Controllers\Healthche…
  GET|HEAD  health-check/error App\Packages\Healthcheck\Controllers\Healthche…
  GET|HEAD  health-check/log App\Packages\Healthcheck\Controllers\Healthcheck…
  GET|HEAD  health-check/migrate App\Packages\Healthcheck\Controllers\Healthc…
  GET|HEAD  health-check/queue-error App\Packages\Healthcheck\Controllers\Hea…
  GET|HEAD  public/{orgId}/emails/{emailId}/attachments/{attachmentId} public…
  GET|HEAD  {orgId}/settings App\Packages\Settings\Controllers\SettingsContro…
  PUT       {orgId}/settings App\Packages\Settings\Controllers\SettingsContro…
  GET|HEAD  {orgId}/tags .. App\Packages\Tags\Controllers\TagsController@index
  DELETE    {orgId}/tags/{id} App\Packages\Tags\Controllers\TagsController@de…
  GET|HEAD  {orgId}/threads App\Packages\Threads\Controllers\ThreadsControlle…
  POST      {orgId}/threads App\Packages\Threads\Controllers\ThreadsControlle…
  GET|HEAD  {orgId}/threads/unread-counts App\Packages\Threads\Controllers\Th…
  GET|HEAD  {orgId}/threads/{threadId} App\Packages\Threads\Controllers\Threa…
  PUT       {orgId}/threads/{threadId} App\Packages\Threads\Controllers\Threa…
  PUT       {orgId}/threads/{threadId}/archive-thread App\Packages\Threads\Co…
  GET|HEAD  {orgId}/threads/{threadId}/contacts App\Packages\Threads\Controll…
  POST      {orgId}/threads/{threadId}/contacts App\Packages\Threads\Controll…
  DELETE    {orgId}/threads/{threadId}/contacts/{threadContactId} App\Package…
  GET|HEAD  {orgId}/threads/{threadId}/events App\Packages\Threads\Controller…
  GET|HEAD  {orgId}/threads/{threadId}/events/{threadEventId} App\Packages\Th…
  POST      {orgId}/threads/{threadId}/messages App\Packages\Threads\Controll…
  POST      {orgId}/threads/{threadId}/notes App\Packages\Threads\Controllers…
  PUT       {orgId}/threads/{threadId}/read-status App\Packages\Threads\Contr…
  POST      {orgId}/threads/{threadId}/tags App\Packages\Threads\Controllers\…
  PUT       {orgId}/threads/{threadId}/unarchive-thread App\Packages\Threads\…
  GET|HEAD  {orgId}/threads/{threadId}/viewers App\Packages\Threads\Controlle…
  GET|HEAD  {orgId}/virtual-mobile-numbers App\Packages\VirtualMobileNumbers\…

*/

export const dateParse = (date: Date | string = '') => {
  if (typeof date === 'string') return parseISO(date)
  return date
}

// Sort inbox threads by most-recent activity (last_thread_event_at), matching
// the backend ordering. Falls back to created_at when the timestamp is missing
// or invalid (dateParse of an empty string yields an Invalid Date, not null).
const sortTs = (t: {
  last_thread_event_at?: Date | null
  created_at: Date
}) => {
  const ts = t.last_thread_event_at?.getTime()
  return ts && !Number.isNaN(ts) ? ts : t.created_at.getTime()
}

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
  }> =>
  async ({ url, method, data, params }) => {
    try {
      const result = await customAxios({
        url: baseUrl + url,
        method,
        data,
        params,
      })
      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError
      if (method !== 'get' && err.response.status !== 401) {
        ServiceErrorHandler(axiosError)
      }

      //
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

const inboxApi = createApi({
  reducerPath: 'inbox',
  refetchOnFocus: false,
  baseQuery: axiosBaseQuery({ baseUrl: config.url.inbox }),
  tagTypes: [
    'counts',
    'settings',
    'threads',
    'selected_thread',
    'thread_events',
    'message_templates',
    'assets',
  ],
  endpoints: (build) => ({
    getUnreadCount: build.query<
      { closed: number; open: number },
      {
        orgId: string
        serial?: string
        context_type?: string
        assignee?: string
        inbox_thread_tag?: string
      }
    >({
      query: ({ orgId, serial, context_type, assignee, inbox_thread_tag }) => ({
        method: 'GET',
        url: buildUrl(`/${orgId}/threads/unread-counts`, {
          serial,
          context_type,
          assignee,
          tag_name: inbox_thread_tag,
        }),
      }),
      providesTags: (_result, _error, arg) => [
        'counts',
        { type: 'counts', id: `${arg.orgId}_${arg.serial}` },
        { type: 'counts', id: 'LIST' },
      ],
    }),

    createNewEmailThread: build.mutation<
      {
        thread: InboxThreadType
      },
      {
        orgId: string
        data: {
          contacts: { email: string; name: string }[]
          serial?: string
          subject: string
          content: string
          // Controls whether the initial event is sent to the customer
          // ('messages') or kept internal ('notes'). Defaults to 'messages'.
          initial_event_type?: 'messages' | 'notes'
          // Format of the initial event body. 'html' preserves rich-text /
          // line-break formatting (email); 'text' for plain SMS. Defaults to
          // 'html' to keep existing callers unchanged.
          format?: 'html' | 'text'
          context_type?: InboxContextTypes
          context_id?: string
        }
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/${orgId}/threads`,
        method: 'POST',
        data: {
          subject: data.subject,
          serial: data.serial,
          context_id: data.context_id,
          context_type: data.context_type,
        },
      }),
      async onQueryStarted(
        { orgId, data: argData },
        { dispatch, queryFulfilled }
      ) {
        const { data } = await queryFulfilled
        const newThread = {
          ...data.thread,
          created_at: dateParse(data.thread.created_at),
          updated_at: dateParse(data.thread.updated_at),
          last_thread_event_at: dateParse(data.thread.last_thread_event_at),
          contacts: [],
          events:
            defaultServicePagination as unknown as ServiceCursorPaginatedResponseType<InboxThreadEventType>,
        } as InboxThreadType

        for await (const contact of argData.contacts) {
          const createContact = await post<{
            thread_contact: InboxThreadContactsType
          }>(
            `/${orgId}/threads/${newThread.id}/contacts`,
            {
              // method: 'email',
              ...contact,
            },
            null,
            'inbox'
          )

          newThread.contacts.push(createContact.thread_contact)
        }

        const eventType = argData.initial_event_type ?? 'messages'
        const threadEvent = await post<{
          message: string
          thread_event: InboxThreadEventType
        }>(
          `/${orgId}/threads/${newThread.id}/${eventType}`,
          { description: argData.content, format: argData.format ?? 'html' },
          null,
          'inbox'
        )

        // newThread.events.data.push(threadEvent.thread_event)
      },
      invalidatesTags: [{ type: 'threads', id: 'LIST' }],
    }),

    getInboxThreads: build.query<
      ServiceCursorPaginatedResponseType<InboxThreadType>,
      {
        orgId: string
        query?: {
          archived?: boolean
          read?: boolean
          serial?: string
          context_type?: InboxContextTypes
          context_id?: string
          cursor?: string
          inbox_thread_tag?: string
          assignee?: string
          /**
           * Every thread this address is a contact on. Backed by a
           * `whereHas` on `thread_contacts.email` in the inbox service.
           */
          contact_email?: string
        }
      }
    >({
      query: ({ orgId, query }) => ({
        url: buildUrl(`/${orgId}/threads`, {
          serial: query?.serial,
          context_type: query?.context_type,
          context_id: query?.context_id,
          assignee: query?.assignee,
          contact_email: query?.contact_email,
          tag_name: query?.inbox_thread_tag,
          cursor: query?.cursor,
          limit: 25,
          // Only send archived/read when explicitly set — the Unread view
          // wants NO archived filter (open + closed unread together), so we
          // must not force archived=0 the way the old code did.
          ...(query?.archived !== undefined
            ? { archived: query.archived ? 1 : 0 }
            : {}),
          ...(query?.read !== undefined ? { read: query.read ? 1 : 0 } : {}),
        }),
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          orgId: queryArgs.orgId,
          params: {
            ...queryArgs.query,
            cursor: '',
          },
        })
      },
      transformResponse: (
        response: ServiceCursorPaginatedResponseType<InboxThreadType>
      ) => {
        const data = [
          ...new Map(
            [...response.data].map((item: any) => [item?.id, item])
          ).values(),
        ]
          .map((item: any) => ({
            ...item,
            created_at: dateParse(item?.created_at),
            archived_at: item?.archived_at
              ? dateParse(item?.archived_at)
              : null,
            read_at: item?.read_at ? dateParse(item?.read_at) : null,
            updated_at: dateParse(item?.updated_at),
            last_thread_event_at: dateParse(item?.last_thread_event_at),
            events: item?.events || defaultServicePagination,
          }))
          .sort((a, b) => sortTs(b) - sortTs(a))

        return { ...response, data }
      },
      merge: (currentCache, newItems) => {
        // Click-through pagination: each page REPLACES the previous one (no
        // appending). The list shows exactly one page at a time, navigated via
        // next_cursor / prev_cursor. Context-scoped consumers only ever load
        // the first page, so replace-semantics are correct for them too.
        currentCache.links = newItems.links
        currentCache.meta = newItems.meta
        currentCache.data = newItems.data
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg?.query?.cursor, previousArg?.query?.cursor)
      },

      providesTags: (items: any) => [
        { type: 'threads', id: 'LIST' },
        ...(items?.data ?? []).map((item: any) => ({
          type: 'threads' as const,
          id: item?.id,
        })),
      ],
    }),

    getInboxThreadEvents: build.query<
      ServiceCursorPaginatedResponseType<InboxThreadEventType> & {
        threadId: string
      },
      { orgId: string; threadId: string; cursor: string }
    >({
      query: ({ orgId, threadId, cursor }) => ({
        url: `/${orgId}/threads/${threadId}/events?cursor=${cursor}`,
        method: 'GET',
      }),
      providesTags: (res, _err, meta) => [
        ...(res?.data ?? []).map((item: any) => ({
          id: item?.id,
          type: 'thread_events' as const,
        })),
        { id: meta.threadId, type: 'thread_events' },
      ],
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          orgId: queryArgs.orgId,
          threadId: queryArgs.threadId,
          params: {
            cursor: '',
          },
        })
      },
      transformResponse: (
        response: ServiceCursorPaginatedResponseType<InboxThreadEventType>,
        _meta,
        arg
      ) => {
        return {
          threadId: arg.threadId,
          ...response,
          data: response.data
            .map((item: any) => ({
              ...item,
              created_at: dateParse(item?.created_at),
              eventable: {
                ...item?.eventable,
                created_at: dateParse(item?.eventable.created_at),
              },
            }))
            .sort((a, b) => a.created_at.getTime() - b.created_at.getTime()),
        }
      },
      merge: (currentCache, newItems) => {
        currentCache.links = newItems.links
        currentCache.meta = newItems.meta

        newItems.data.forEach((item: any) => {
          const current = currentCache.data.findIndex(
            (it: any) => it?.id === item?.id
          )
          if (current >= 0) {
            currentCache.data[current] = item
          } else {
            currentCache.data.push(item)
          }
        })

        currentCache.data = currentCache.data.sort(
          (a, b) => a.created_at.getTime() - b.created_at.getTime()
        )
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg?.cursor, previousArg?.cursor)
      },
    }),

    getInboxThreadLatestEvent: build.query<
      { is_public_outgoing_event: boolean } | null,
      { orgId: string; threadId: string }
    >({
      query: ({ orgId, threadId }) => ({
        url: `/${orgId}/threads/${threadId}/events?per_page=10`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        const messageTypes = [
          'Message',
          'Email',
          'Incoming SMS Message',
          'Incoming Form Submission',
          'Contact Reply',
        ]
        const event = (response?.data ?? []).find((e: any) =>
          messageTypes.includes(e.eventable_type)
        )
        if (!event) return null
        return {
          is_public_outgoing_event: event.is_public_outgoing_event ?? false,
        }
      },
      providesTags: (_res, _err, meta) => [
        { id: meta.threadId, type: 'thread_events' },
      ],
    }),

    getInboxThread: build.query<
      InboxThreadType,
      { orgId: string; threadId: string }
    >({
      query: ({ orgId, threadId }) => {
        return {
          url: `/${orgId}/threads/${threadId}`,
          method: 'GET',
        }
      },
      transformResponse: (response: { data: InboxThreadType }) => {
        return {
          ...response.data,
          created_at: dateParse(response.data.created_at),
          archived_at: response.data.archived_at
            ? dateParse(response.data.archived_at)
            : null,
          read_at: response.data.read_at
            ? dateParse(response.data.read_at)
            : null,
          updated_at: dateParse(response.data.updated_at),
          last_thread_event_at: dateParse(response.data.last_thread_event_at),
        }
      },
      providesTags: (_1, _2, meta) => [
        { type: 'selected_thread', id: meta.threadId },
      ],
    }),

    getInboxThreadsLastEventsV2: build.query<
      Record<string, InboxThreadLastEventV2>,
      { orgId: string; threadIds: string[] }
    >({
      query: ({ orgId, threadIds }) => ({
        url: `/${orgId}/threads/last-events/v2`,
        method: 'POST',
        data: { thread_ids: threadIds },
      }),
      transformResponse: (response: InboxThreadsLastEventsV2Response) =>
        (response.threads ?? []).reduce<Record<string, InboxThreadLastEventV2>>(
          (acc, t) => {
            acc[t.id] = t
            return acc
          },
          {}
        ),
      serializeQueryArgs: ({ queryArgs }) =>
        JSON.stringify({
          orgId: queryArgs.orgId,
          threadIds: [...queryArgs.threadIds].sort(),
        }),
      providesTags: (_res, _err, arg) =>
        arg.threadIds.map((id) => ({
          type: 'selected_thread' as const,
          id,
        })),
    }),

    sendInboxThreadEvents: build.mutation<
      InboxThreadEventType,
      {
        orgId: string
        threadId: string
        type: 'messages' | 'notes'
        description: string
        format: 'html' | 'text'
      }
    >({
      query: ({ orgId, threadId, type, description, format }) => ({
        url: `/${orgId}/threads/${threadId}/${type}`,
        method: 'POST',
        data: { description, format },
      }),

      transformResponse: (response: {
        message: string
        thread_event: InboxThreadEventType
      }) => response.thread_event,
      async onQueryStarted(
        { orgId, threadId, type },
        { dispatch, getState, queryFulfilled }
      ) {
        const { data } = await queryFulfilled

        const formattedData = {
          ...data,
          created_at: dateParse(data.created_at),
          eventable: {
            ...data.eventable,
            created_at: dateParse(data.eventable.created_at),
          },
        } as InboxThreadEventType

        dispatch(
          inboxApi.util.updateQueryData(
            'getInboxThreadEvents',
            { orgId, threadId, cursor: '' },
            (draft: any) => {
              const findIndex = draft.data.findIndex(
                (i: any) => i?.id === data.id
              )

              if (findIndex >= 0) {
                draft.data[findIndex] = formattedData
              } else {
                draft.data.push(formattedData)
              }
            }
          )
        )

        dispatch(
          inboxApi.util.updateQueryData(
            'getInboxThread',
            { orgId, threadId },
            (draft: any) => {
              draft.last_thread_event_at = new Date()
              draft.last_public_outgoing_event_at = new Date().toISOString()
            }
          )
        )

        // A public reply ('messages') flips the thread to "waiting on guest".
        // Patch every cached last-events batch so the reply-status pills and the
        // day-planner red badge update immediately — without waiting for (and
        // racing) a replica-read refetch. Internal notes don't change this.
        if (type === 'messages') {
          const nowIso = new Date().toISOString()
          const queries =
            (getState() as any)[inboxApi.reducerPath]?.queries ?? {}
          for (const entry of Object.values(queries)) {
            if (entry?.endpointName !== 'getInboxThreadsLastEventsV2') continue
            dispatch(
              inboxApi.util.updateQueryData(
                'getInboxThreadsLastEventsV2',
                entry.originalArgs,
                (draft: any) => {
                  if (draft?.[threadId]) {
                    draft[threadId].last_public_outgoing_event_at = nowIso
                    draft[threadId].read_at = nowIso
                  }
                }
              )
            )
          }
        }
      },
    }),

    createEmailContactsOnThread: build.mutation<
      {
        thread_contact: InboxThreadContactsType
        message: string
      },
      {
        orgId: string
        threadId: string
        data: {
          method: 'email'
          email: string
          name: string
        }
      }
    >({
      query: ({ orgId, threadId, data }) => ({
        url: `/${orgId}/threads/${threadId}/contacts`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ orgId, threadId }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled

        dispatch(
          inboxApi.util.updateQueryData(
            'getInboxThread',
            { orgId, threadId },
            (draft: any) => {
              draft.contacts.push(data.thread_contact)
            }
          )
        )
      },
    }),

    removeEmailContactsOnThread: build.mutation<
      unknown,
      { orgId: string; threadId: string; contactId: string }
    >({
      query: ({ orgId, threadId, contactId }) => ({
        url: `/${orgId}/threads/${threadId}/contacts/${contactId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { orgId, threadId, contactId },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          inboxApi.util.updateQueryData(
            'getInboxThread',
            { orgId, threadId },
            (draft: any) => {
              draft.contacts = draft.contacts.filter(
                (i: any) => i.id !== contactId
              )
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),

    assignInboxThread: build.mutation<
      { message: string; assignee: InboxThreadAssigneeType },
      {
        orgId: string
        threadId: string
        data: { oauth_user_id: string; label: 'owner' | 'subscriber' }
      }
    >({
      query: ({ orgId, threadId, data }) => ({
        url: `/${orgId}/threads/${threadId}/assignees`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_1, _2, meta) => [
        { type: 'selected_thread', id: meta.threadId },
      ],
    }),

    unassignInboxThread: build.mutation<
      { data: InboxThreadEventType },
      {
        orgId: string
        threadId: string
        threadAssigneeId: string
      }
    >({
      query: ({ orgId, threadId, threadAssigneeId }) => ({
        url: `/${orgId}/threads/${threadId}/assignees/${threadAssigneeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_1, _2, meta) => [
        { type: 'selected_thread', id: meta.threadId },
      ],
    }),

    archiveInboxThread: build.mutation<
      { message: string; thread: InboxThreadType },
      {
        orgId: string
        threadId: string
        archive: boolean
        query: {
          archived?: boolean
          serial?: string
          context_type?: InboxContextTypes
          context_id?: string
          cursor?: string
          inbox_thread_tag?: string
          assignee?: string
        }
      }
    >({
      query: ({ orgId, threadId, archive }) => ({
        url: `/${orgId}/threads/${threadId}/${
          archive ? 'archive-thread' : 'unarchive-thread'
        }`,
        method: 'PUT',
      }),
      async onQueryStarted(
        { orgId, threadId, archive },
        { dispatch, getState, queryFulfilled }
      ) {
        // Optimistically set archived_at everywhere this thread is cached. The
        // list filters Open/Closed by archived_at client-side, so it leaves the
        // current view instantly — without a server refetch that can read a
        // lagging replica and put it straight back.
        const newArchivedAt = archive ? new Date() : null
        const patches: { undo: () => void }[] = []

        patches.push(
          dispatch(
            inboxApi.util.updateQueryData(
              'getInboxThread',
              { orgId, threadId },
              (draft: any) => {
                draft.archived_at = newArchivedAt
              }
            )
          )
        )

        const newArchivedIso = archive ? new Date().toISOString() : null

        // ── zion's enquiry caches ────────────────────────────────────────
        //
        // Closing a thread is what takes an enquiry out of the needs-reply
        // counts, and those live in zion. INVALIDATING THEM HERE DOES NOT
        // WORK: zion does not learn about the close from this request, it
        // learns from the system note Inbox raises, over SQS. A refetch
        // fired now races that and pulls the old row straight back, so the
        // button keeps saying "Close thread" and the count does not move
        // until you reload.
        //
        // So patch them, the same way the inbox caches above are patched,
        // and let the next natural read reconcile - exactly the reasoning in
        // `invalidatesTags` below.
        const zionQueries =
          (getState() as any)[zionApi.reducerPath]?.queries ?? {}
        /** The enquiry this thread belongs to, learned from the list cache. */
        let closedEnquiryStatus: string | null = null

        for (const entry of Object.values(zionQueries) as any[]) {
          if (entry?.endpointName !== 'getEnquiries') continue
          patches.push(
            dispatch(
              zionApi.util.updateQueryData(
                'getEnquiries',
                entry.originalArgs,
                (draft: any) => {
                  const row = draft.data?.find(
                    (e: any) => e?.inbox_thread_id === threadId
                  )
                  if (!row) return
                  if (archive && row.thread_state?.needs_reply) {
                    closedEnquiryStatus = row.status ?? null
                  }
                  if (row.thread_state) {
                    row.thread_state.archived_at = newArchivedIso
                    // Reopening does not make a reply owed again; only the
                    // guest speaking does, and zion decides that.
                    if (archive) row.thread_state.needs_reply = false
                  }
                  if (archive) row.needs_reply = false
                }
              )
            )
          )
        }

        // The badge reads `status_summary` off a needs_reply=true counts
        // call, so take the enquiry out of its own status bucket. Only on
        // close, and only when it was actually raised.
        if (archive && closedEnquiryStatus) {
          const status: string = closedEnquiryStatus
          for (const entry of Object.values(zionQueries) as any[]) {
            if (entry?.endpointName !== 'getEnquiryCounts') continue
            patches.push(
              dispatch(
                zionApi.util.updateQueryData(
                  'getEnquiryCounts',
                  entry.originalArgs,
                  (draft: any) => {
                    const summary = draft?.counts?.status_summary
                    if (!summary || typeof summary[status] !== 'number') return
                    summary[status] = Math.max(0, summary[status] - 1)
                  }
                )
              )
            )
          }
        }

        const queries = (getState() as any)[inboxApi.reducerPath]?.queries ?? {}
        for (const entry of Object.values(queries)) {
          if (entry?.endpointName === 'getInboxThreads') {
            patches.push(
              dispatch(
                inboxApi.util.updateQueryData(
                  'getInboxThreads',
                  entry.originalArgs,
                  (draft: any) => {
                    const t = draft.data?.find((i: any) => i?.id === threadId)
                    if (t) t.archived_at = newArchivedAt
                  }
                )
              )
            )
          }
          // Keep the batched last-events caches (day-planner inbox, enquiries)
          // in sync so closing/reopening moves the thread between Open/Closed
          // and updates the awaiting-reply badge without a refetch.
          if (entry?.endpointName === 'getInboxThreadsLastEventsV2') {
            patches.push(
              dispatch(
                inboxApi.util.updateQueryData(
                  'getInboxThreadsLastEventsV2',
                  entry.originalArgs,
                  (draft: any) => {
                    if (draft?.[threadId]) {
                      draft[threadId].archived_at = newArchivedIso
                    }
                  }
                )
              )
            )
          }
        }

        try {
          await queryFulfilled
          toast.success(`Thread ${archive ? 'closed' : 'opened'}`)
        } catch {
          patches.forEach((p) => {
            p.undo()
          })
        }
      },
      // Only counts need a server refresh; invalidating the list/thread tags
      // would trigger a replica-read refetch that undoes the optimistic state.
      invalidatesTags: ['counts'],
    }),

    markInboxThreadAsRead: build.mutation<
      {
        thread: Omit<InboxThreadType, 'events'>
      },
      {
        orgId: string
        threadId: string
        read: boolean
      }
    >({
      query: ({ orgId, threadId, read }) => ({
        url: `/${orgId}/threads/${threadId}/read-status`,
        method: 'PUT',
        data: { read },
      }),
      async onQueryStarted(
        { orgId, threadId, read },
        { dispatch, getState, queryFulfilled }
      ) {
        // Optimistically set read_at everywhere this thread is cached. The list
        // filters the Unread view by read_at client-side, so this drops it from
        // Unread instantly — without waiting on a server refetch that can read a
        // lagging replica and resurrect the thread as unread.
        const newReadAt = read ? new Date() : null
        const patches: { undo: () => void }[] = []

        patches.push(
          dispatch(
            inboxApi.util.updateQueryData(
              'getInboxThread',
              { orgId, threadId },
              (draft: any) => {
                draft.read_at = newReadAt
              }
            )
          )
        )

        const queries = (getState() as any)[inboxApi.reducerPath]?.queries ?? {}
        for (const entry of Object.values(queries)) {
          if (entry?.endpointName !== 'getInboxThreads') continue
          patches.push(
            dispatch(
              inboxApi.util.updateQueryData(
                'getInboxThreads',
                entry.originalArgs,
                (draft: any) => {
                  const t = draft.data?.find((i: any) => i?.id === threadId)
                  if (t) t.read_at = newReadAt
                }
              )
            )
          )
        }

        try {
          await queryFulfilled
        } catch {
          patches.forEach((p) => {
            p.undo()
          })
        }
      },
      // Note: intentionally NOT invalidating the thread/list tags here — that
      // would trigger a refetch off a possibly-lagging replica and undo the
      // optimistic read state. Only counts need refreshing.
      invalidatesTags: ['counts'],
    }),

    generateInboxThreadShareLink: build.mutation<
      { message: string; thread_id: string; public_url: string },
      { orgId: string; threadId: string }
    >({
      query: ({ orgId, threadId }) => ({
        url: `/${orgId}/threads/${threadId}/tokens`,
        method: 'POST',
        data: {},
      }),
    }),

    /**
     *  SETTINGS ETC
     */

    getInboxSettings: build.query<InboxSettingsDataType, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/${orgId}/settings`,
        method: 'GET',
      }),
      providesTags: ['settings'],
      transformResponse: (response: { settings: InboxSettingsDataType }) => {
        return response.settings
      },
    }),

    updateInboxSettings: build.mutation<
      InboxSettingsDataType,
      { orgId: string; settings: InboxSettingsDataType }
    >({
      query: ({ orgId, settings }) => ({
        url: `/${orgId}/settings`,
        method: 'PUT',
        data: settings,
      }),
      async onQueryStarted({ orgId, settings }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          inboxApi.util.updateQueryData(
            'getInboxSettings',
            { orgId },
            (draft: any) => {
              draft = settings
              return draft
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      transformResponse: (response: {
        message: string
        settings: InboxSettingsDataType
      }) => {
        toast.success(response.message)
        return response.settings
      },
    }),

    getVirtualNumbers: build.query<
      ServiceCursorPaginatedResponseType<VirtualNumberType>,
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/virtual-mobile-numbers`,
        method: 'GET',
      }),
    }),

    /**
     *  SETTINGS END
     */

    /**
     *  TAGS
     */

    createTagOnThread: build.mutation<
      InboxTagType,
      { orgId: string; threadId: string; data: { name: string } }
    >({
      query: ({ orgId, threadId, data }) => ({
        url: `/${orgId}/threads/${threadId}/tags`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: { tag: InboxTagType }) => {
        return response.tag
      },
      async onQueryStarted({ orgId, threadId }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled

        dispatch(
          inboxApi.util.updateQueryData(
            'getInboxThread',
            { orgId, threadId },
            (draft: any) => {
              draft.tags.push(data)
            }
          )
        )
      },
    }),

    deleteThreadTag: build.mutation<
      { message: string },
      { orgId: string; tagId: string; threadId: string }
    >({
      query: ({ orgId, tagId }) => ({
        url: `/${orgId}/tags/${tagId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { orgId, tagId, threadId },
        { dispatch, queryFulfilled }
      ) {
        const patchResponse = dispatch(
          inboxApi.util.updateQueryData(
            'getInboxThread',
            { orgId, threadId },
            (draft: any) => {
              draft.tags = draft.tags.filter((t: any) => t.id !== tagId)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResponse.undo()
        }
      },
    }),

    getThreadTags: build.query<
      FlatServiceCursorPaginatedResponseType<InboxTagType>,
      { orgId: string; query?: { tags_query?: string; cursor?: string } }
    >({
      query: ({ orgId, query }) => ({
        url: buildUrl(`/${orgId}/tags`, {
          name_starts_with: query?.tags_query,
          cursor: query?.cursor,
        }),
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          orgId: queryArgs.orgId,
          params: {
            ...queryArgs.query,
            cursor: '',
          },
        })
      },
      merge: (currentCache, newItems) => {
        currentCache.next_cursor = newItems.next_cursor
        currentCache.prev_cursor = newItems.prev_cursor
        currentCache.next_page_url = newItems.next_page_url
        currentCache.prev_page_url = newItems.prev_page_url
        currentCache.path = newItems.path
        currentCache.per_page = newItems.per_page

        newItems.data.forEach((item: any) => {
          currentCache.data.push(item)
        })
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg?.query?.cursor, previousArg?.query?.cursor)
      },
    }),
    /**
     *  TAGS END
     */

    /**
     *  MESSAGE TEMPLATES
     */

    getMessageTemplates: build.query<
      ServiceCursorPaginatedResponseType<InboxMessageTemplateType>,
      { orgId: string; query?: { serial?: string; name_starts_with?: string } }
    >({
      query: ({ orgId, query }) => ({
        url: buildUrl(`/${orgId}/message-templates`, {
          serial: query?.serial,
          name_starts_with: query?.name_starts_with,
        }),
        method: 'GET',
      }),
      providesTags: (result) => [
        { type: 'message_templates', id: 'LIST' },
        ...(result?.data ?? []).map((template) => ({
          type: 'message_templates' as const,
          id: template.id,
        })),
      ],
    }),

    createMessageTemplate: build.mutation<
      InboxMessageTemplateType,
      { orgId: string; data: InboxMessageTemplatePayload }
    >({
      query: ({ orgId, data }) => ({
        url: `/${orgId}/message-templates`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: {
        message_template: InboxMessageTemplateType
      }) => response.message_template,
      invalidatesTags: [{ type: 'message_templates', id: 'LIST' }],
    }),

    updateMessageTemplate: build.mutation<
      InboxMessageTemplateType,
      { orgId: string; templateId: string; data: InboxMessageTemplatePayload }
    >({
      query: ({ orgId, templateId, data }) => ({
        url: `/${orgId}/message-templates/${templateId}`,
        method: 'PUT',
        data,
      }),
      transformResponse: (response: {
        message_template: InboxMessageTemplateType
      }) => response.message_template,
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'message_templates', id: 'LIST' },
        { type: 'message_templates', id: templateId },
      ],
    }),

    deleteMessageTemplate: build.mutation<
      { message: string },
      { orgId: string; templateId: string }
    >({
      query: ({ orgId, templateId }) => ({
        url: `/${orgId}/message-templates/${templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'message_templates', id: 'LIST' }],
    }),

    // Renders a template's merge tags against a thread, returning text ready to
    // drop into the composer. Used on demand via the lazy hook from the picker.
    renderMessageTemplate: build.query<
      InboxRenderedTemplate,
      { orgId: string; threadId: string; templateId: string }
    >({
      query: ({ orgId, threadId, templateId }) => ({
        url: `/${orgId}/threads/${threadId}/templates/${templateId}/render`,
        method: 'GET',
      }),
    }),

    /**
     *  MESSAGE TEMPLATES END
     */

    /**
     *  ASSETS (media library)
     */

    getAssets: build.query<
      ServiceCursorPaginatedResponseType<InboxAssetType>,
      {
        orgId: string
        query?: { serial?: string; folder?: string; name_starts_with?: string }
      }
    >({
      query: ({ orgId, query }) => ({
        url: buildUrl(`/${orgId}/assets`, {
          serial: query?.serial,
          folder: query?.folder,
          name_starts_with: query?.name_starts_with,
        }),
        method: 'GET',
      }),
      providesTags: (result) => [
        { type: 'assets', id: 'LIST' },
        ...(result?.data ?? []).map((asset) => ({
          type: 'assets' as const,
          id: asset.id,
        })),
      ],
    }),

    // Step 1 of the upload dance: returns a presigned PUT url the browser
    // uploads to directly (see uploadAssetToS3 helper), then confirm.
    initiateAssetUpload: build.mutation<
      InboxAssetInitiateResponse,
      { orgId: string; data: InboxAssetInitiatePayload }
    >({
      query: ({ orgId, data }) => ({
        url: `/${orgId}/assets`,
        method: 'POST',
        data,
      }),
    }),

    // Step 3: tell the server the direct upload finished; marks it live.
    confirmAssetUpload: build.mutation<
      { message: string; asset: InboxAssetType },
      { orgId: string; assetId: string }
    >({
      query: ({ orgId, assetId }) => ({
        url: `/${orgId}/assets/${assetId}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'assets', id: 'LIST' }],
    }),

    deleteAsset: build.mutation<
      { message: string },
      { orgId: string; assetId: string }
    >({
      query: ({ orgId, assetId }) => ({
        url: `/${orgId}/assets/${assetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'assets', id: 'LIST' }],
    }),

    /**
     *  ASSETS END
     */

    /**
     *  UNUSED
     */

    getInboxThreadEvent: build.query<
      InboxThreadEventType,
      { orgId: string; threadId: string; eventId: string }
    >({
      query: ({ orgId, threadId, eventId }) => ({
        url: `/${orgId}/threads/${threadId}/events/${eventId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: InboxThreadEventType }) => {
        return response.data
      },
      async onQueryStarted({ orgId, threadId }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled

        const payload = {
          ...data,
          created_at: dateParse(data.created_at),
          eventable: {
            ...data.eventable,
            created_at: dateParse(data.eventable.created_at),
          },
        } as InboxThreadEventType

        dispatch(
          inboxApi.util.updateQueryData(
            'getInboxThreadEvents',
            { orgId, threadId, cursor: '' },
            (draft: any) => {
              const findIndex = draft.data.findIndex(
                (i: any) => i?.id === data.id
              )

              if (findIndex >= 0) {
                draft.data[findIndex] = payload
              } else {
                draft.data.push(payload)
              }
            }
          )
        )
      },
    }),

    createThread: build.mutation<
      Omit<InboxThreadType, 'contacts' | 'events'>,
      {
        orgId: string
        data: {
          serial?: string
          subject: string
          context_type?: 'booking' | 'review'
          context_id?: string
        }
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/${orgId}/threads`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: {
        thread: Omit<InboxThreadType, 'contacts' | 'events'>
      }) => response.thread,
      async onQueryStarted({ orgId }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled

        /**
         * @todo would need query
         */

        dispatch(
          inboxApi.util.updateQueryData(
            'getInboxThreads',
            { orgId },
            (draft: any) => {
              draft.data.unshift({
                ...data,
                contacts: [],
                // @todo
                events:
                  defaultServicePagination as unknown as ServiceCursorPaginatedResponseType<InboxThreadEventType>,
              })
            }
          )
        )
      },
    }),

    updateInboxThread: build.mutation<
      InboxThreadType,
      {
        orgId: string
        threadId: string
        thread: Pick<
          InboxThreadType,
          'serial' | 'context_type' | 'context_id' | 'assignees' | 'subject'
        >
      }
    >({
      query: ({ orgId, threadId, thread }) => ({
        url: `/${orgId}/threads/${threadId}`,
        method: 'PUT',
        data: { ...thread },
      }),
      transformResponse: (response: {
        thread: InboxThreadType
        message: string
      }) => response.thread,
      async onQueryStarted({ orgId, threadId }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled

        /**
         * @todo query
         */

        dispatch(
          inboxApi.util.updateQueryData(
            'getInboxThreads',
            { orgId },
            (draft: any) => {
              const idx = draft.data.findIndex(
                (thread: any) => thread?.id === data.id
              )
              if (idx !== -1) {
                draft.data[idx] = {
                  ...data,
                  created_at: dateParse(data.created_at),
                  archived_at: data.archived_at
                    ? dateParse(data.archived_at)
                    : null,
                  read_at: data.read_at ? dateParse(data.read_at) : null,
                  updated_at: dateParse(data.updated_at),
                  last_thread_event_at: dateParse(data.last_thread_event_at),
                  events: draft.data[idx]?.events || defaultServicePagination,
                  isLoading: false,
                }
              }
            }
          )
        )
      },
    }),

    /**
     *  UNUSED END
     */
  }),
})

export default inboxApi
